import React, { useContext, useEffect, useState } from 'react'
import { OvertimeClaimContext } from './OvertimeClaims'
import {
  Alert,
  AlertTitle,
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  InputAdornment,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { Cancel, Check, Close, Save, Undo } from '@mui/icons-material'
import moment from 'moment'
import { TenantSettingsContext } from '../../../../../App'
import API from '../../../../../services/API'
import { getCurrencySymbol } from '../../../../../helpers/currency'

const Row = ({ title, value }) => (
  <Stack
    direction={{ xs: 'column', md: 'row' }}
    alignItems="flex-start"
    justifyContent="space-between"
  >
    <Typography flex={1} fontWeight={100} variant="body2">
      {title}
    </Typography>
    <Stack flex={1} alignItems="flex-end">
      <Typography flex={1} fontWeight={800} variant="body2" textAlign="right">
        {value}
      </Typography>
    </Stack>
  </Stack>
)

const SelectedShiftDrawer = () => {
  const { selectedShift, setSelectedShift, loadClaims } =
    useContext(OvertimeClaimContext)
  const { orgSettings } = useContext(TenantSettingsContext)?.orgConfig
  const currencySymbol = getCurrencySymbol(orgSettings?.currency)
  const [payableMinutes, setPayableMinutes] = useState(0)
  const [payableRate, setPayableRate] = useState(0)
  const [notes, setNotes] = useState('')
  const [openConfirm, setOpenConfirm] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState('')
  const [undoConfirmOpen, setUndoConfirmOpen] = useState(false)
  const [undoError, setUndoError] = useState('')

  useEffect(() => {
    // Always reset the form from the incoming shift - a rate of 0 is a
    // legitimate value (fixed-pay shifts default to it), so bailing out on
    // falsy values left the previous claim's minutes and rate in the form
    setPayableMinutes(
      selectedShift?.overTime?.decidedMinutes ??
        selectedShift?.overTime?.roundedMinutes ??
        0,
    )
    setPayableRate(selectedShift?.overTime?.rate ?? 0)
    setNotes('')
    setSubmitError('')
    setUndoError('')
    setUndoConfirmOpen(false)
  }, [selectedShift])

  const handleSubmit = () => {
    setSubmitting(true)
    setSubmitError('')
    API.post(`/overtime-claims-update/${selectedShift._id}`, {
      status: openConfirm,
      rate: payableRate,
      decidedMinutes: payableMinutes,
      notes,
    })
      .then((res) => {
        if (res.status === 200) {
          setOpenConfirm('')
          setSelectedShift(null)
          loadClaims()
        } else {
          setSubmitError(
            res.data?.error ??
              'Unable to update this overtime claim. Please try again.',
          )
        }
      })
      .catch(() =>
        setSubmitError(
          'Unable to update this overtime claim. Please try again.',
        ),
      )
      .finally(() => setSubmitting(false))
  }

  const handleUndo = () => {
    setSubmitting(true)
    API.post(`/overtime-claims-undo/${selectedShift._id}`).then((res) => {
      setSubmitting(false)
      if (res.status === 200) {
        setUndoConfirmOpen(false)
        setSelectedShift(null)
        loadClaims()
      } else {
        setUndoError(res.data?.error || 'Unable to undo this decision.')
      }
    })
  }

  return (
    <Drawer
      anchor="right"
      open={!!selectedShift}
      onClose={() => setSelectedShift(null)}
      PaperProps={{
        sx: {
          width: { xs: '80%', sm: 600, lg: 400 },
          // Pull away from edges
          top: 8,
          right: 8,
          bottom: 8,
          // Important - stop it filling the viewport
          boxShadow:
            '0 24px 64px rgba(15,23,42,0.18), 0 8px 24px rgba(15,23,42,0.12)',
          border: '1px solid',
          borderColor: 'divider',
          m: 2,
          height: 'calc(100% - 80px)',
          borderRadius: 4,
          overflow: 'auto',
          padding: 2,
        },
      }}
    >
      <Stack gap={2} justifyContent="space-between">
        <Stack gap={2}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="title3">Overtime Claim Details</Typography>
            <IconButton onClick={() => setSelectedShift(null)}>
              <Close />
            </IconButton>
          </Stack>
          <Stack direction="row" gap={1}>
            <Avatar src={selectedShift?.assignedTo?.userAccount?.photograph} />
            <Stack flex={1}>
              <Typography variant="body2">
                {selectedShift?.assignedTo?.userAccount?.firstName}{' '}
                {selectedShift?.assignedTo?.userAccount?.lastName}
              </Typography>
              <Typography variant="caption">
                {selectedShift?.assignedTo?.clinicalRole}
              </Typography>
            </Stack>
            <Chip
              color={
                selectedShift?.ptsDuty
                  ? 'anPurple'
                  : selectedShift?.event
                    ? 'anRed'
                    : selectedShift?.frontLine
                      ? 'secondary'
                      : 'contrast'
              }
              label={
                selectedShift?.ptsDuty
                  ? 'PTS'
                  : selectedShift?.event
                    ? 'Event'
                    : selectedShift?.frontLine
                      ? 'Front Line'
                      : 'Admin'
              }
              variant="filled"
            />
            <Chip
              variant="filled"
              color={
                selectedShift?.overTime?.status === 'Rejected'
                  ? 'error'
                  : selectedShift?.overTime?.status === 'Approved'
                    ? 'success'
                    : 'contrast'
              }
              label={selectedShift?.overTime?.status}
            />
          </Stack>
          <Divider />
          <Stack gap={1}>
            <Typography variant="body2" fontWeight={600} fontSize="0.9rem">
              Shift Details
            </Typography>
            <Row
              title="Shift Date"
              value={moment(selectedShift?.start)
                .format('Do MMM YYYY')
                .toString()}
            />
            <Row
              title="Scheduled"
              value={`${moment(selectedShift?.start).format('HH:mm').toString()} - ${moment(selectedShift?.end).format('HH:mm').toString()}`}
            />
            <Row
              title="Clock"
              value={`${selectedShift?.clock?.startedAt ? moment(selectedShift.clock.startedAt).format('HH:mm').toString() : '-'} - ${selectedShift?.clock?.endedAt ? moment(selectedShift?.clock?.endedAt).format('HH:mm').toString() : '-'}`}
            />
            {selectedShift?.clock?.lateStartReason && (
              <Row
                title="Late Start"
                value={selectedShift.clock.lateStartReason}
              />
            )}
            <Row
              title="Actual Finish"
              value={moment(selectedShift?.overTime?.actualFinishTime)
                .format('HH:mm')
                .toString()}
            />
          </Stack>
          <Divider />
          <Stack gap={1}>
            <Typography variant="body2" fontWeight={600} fontSize="0.9rem">
              Claim Information
            </Typography>
            <Row
              title="Reason"
              value={
                selectedShift?.clock?.otherReason ??
                selectedShift?.clock?.correctedReason ??
                selectedShift?.clock?.lateEndReason
              }
            />
            <Row
              title="Raw Overtime"
              value={`${selectedShift?.overTime?.actualMinutes} min`}
            />
            <Row
              title="Rounded (Claimed)"
              value={`${selectedShift?.overTime?.roundedMinutes}`}
            />
            <Row
              title="Rounding Rule"
              value={selectedShift?.overTime?.roundingRule}
            />
          </Stack>
          <Divider />
          <Stack gap={1}>
            <Typography variant="body2" fontWeight={600} fontSize="0.9rem">
              Financial Impact
            </Typography>
            <Row
              title="Pay Rate"
              value={
                !selectedShift?.hourlyRate
                  ? 'Shift Rate (Not Hourly)'
                  : `${currencySymbol}${selectedShift?.overTime?.rate ?? 0}/hr`
              }
            />
            <Row
              title="Payable"
              value={
                !selectedShift?.hourlyRate
                  ? 'TBC'
                  : `${selectedShift?.overTime?.roundedMinutes}min (${(selectedShift?.overTime?.roundedMinutes / 60).toFixed(2)}hrs)`
              }
            />
            <Row
              title="Estimated Cost"
              value={
                !selectedShift?.hourlyRate
                  ? 'N/A'
                  : `${currencySymbol}${(((selectedShift?.overTime?.rate ?? 0) * (selectedShift?.overTime?.roundedMinutes ?? 0)) / 60).toFixed(2)}`
              }
            />
          </Stack>
          <Divider />
          <Stack gap={1}>
            <Typography variant="body2" fontWeight={600} fontSize="0.9rem">
              Manager Decision
            </Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} alignItems="center">
              <Typography flex={1} fontWeight={100} variant="body2">
                Payable Minutes
              </Typography>
              <TextField
                value={payableMinutes}
                onChange={(e) => setPayableMinutes(e.target.value)}
                type="number"
                inputProps={{ min: 0 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">min</InputAdornment>
                  ),
                }}
                size="small"
                sx={{
                  flex: 0.7,
                }}
              />
            </Stack>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              alignItems="center"
              mb={1}
            >
              <Typography flex={1} fontWeight={100} variant="body2">
                Payable Rate
              </Typography>
              <TextField
                value={payableRate}
                onChange={(e) => setPayableRate(e.target.value)}
                type="number"
                required
                inputProps={{ min: 0 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {currencySymbol}
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">hr</InputAdornment>
                  ),
                }}
                helperText="Required to approve — fixed-pay shifts default to 0, so set the hourly rate the overtime should pay"
                size="small"
                sx={{
                  flex: 0.7,
                }}
              />
            </Stack>
            <Row
              title="Amount To Pay"
              value={`${currencySymbol}${(((payableMinutes ?? 0) * (payableRate ?? 0)) / 60).toFixed(2)}`}
            />
            <Stack mt={1} gap={0.5}>
              <Typography flex={1} fontWeight={100} variant="body2">
                Manager Notes (optional)
              </Typography>
              <TextField
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                multiline
                minRows={3}
                placeholder="Add notes (optional)..."
              />
            </Stack>
          </Stack>
        </Stack>
        {selectedShift?.overTime?.status === 'Pending' && (
          <Stack direction={{ xs: 'column', lg: 'row' }} gap={0.7} my={2}>
            <Button
              color="anRed"
              startIcon={<Cancel />}
              variant="contained"
              fullWidth
              onClick={() => setOpenConfirm('Rejected')}
            >
              Reject Claim
            </Button>
            <Button
              color="success"
              startIcon={<Check />}
              variant="contained"
              fullWidth
              onClick={() => setOpenConfirm('Approved')}
            >
              Approve Claim
            </Button>
          </Stack>
        )}
        {selectedShift?.overTime?.status !== 'Pending' && (
          <Stack gap={1}>
            <Alert
              severity={
                selectedShift?.overTime?.status === 'Approved'
                  ? 'success'
                  : 'error'
              }
            >
              <AlertTitle>{selectedShift?.overTime?.status}</AlertTitle>
              By {selectedShift?.overTime?.decidedBy?.firstName}{' '}
              {selectedShift?.overTime?.decidedBy?.lastName} on{' '}
              {moment(selectedShift?.overTime?.decidedAt)
                .format('Do MMM YYYY [at] HH:mm')
                .toString()}
            </Alert>
            {undoError && <Alert severity="error">{undoError}</Alert>}
            <Button
              color="contrast"
              variant="outlined"
              startIcon={<Undo />}
              onClick={() => setUndoConfirmOpen(true)}
            >
              Undo Decision
            </Button>
          </Stack>
        )}
      </Stack>
      <Dialog
        open={!!openConfirm && selectedShift?.overTime?.status === 'Pending'}
        onClose={() => !submitting && setOpenConfirm(null)}
      >
        <DialogTitle>Confirm Overtime Request</DialogTitle>
        <DialogContent>
          <Stack gap={2}>
            <Typography>
              Are you sure you want to mark this overtime request as{' '}
              {openConfirm}?
            </Typography>
            {submitError && <Alert severity="error">{submitError}</Alert>}
            {submitting && <LinearProgress color="contrast" />}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => !submitting && setOpenConfirm(null)}
            color="contrast"
            variant="outlined"
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleSubmit()}
            color={openConfirm === 'Rejected' ? 'error' : 'success'}
            variant="contained"
            disabled={submitting}
          >
            Yes - {openConfirm === 'Rejected' ? 'Reject' : 'Approve'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={undoConfirmOpen}
        onClose={() => !submitting && setUndoConfirmOpen(false)}
      >
        <DialogTitle>Undo Overtime Decision</DialogTitle>
        <DialogContent>
          <Stack gap={2}>
            <Typography>
              This will reset the claim back to Pending so it can be reviewed
              again.
            </Typography>
            {undoError && <Alert severity="error">{undoError}</Alert>}
            {submitting && <LinearProgress color="contrast" />}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => !submitting && setUndoConfirmOpen(false)}
            color="contrast"
            variant="outlined"
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUndo}
            color="anRed"
            variant="contained"
            disabled={submitting}
          >
            Yes - Undo
          </Button>
        </DialogActions>
      </Dialog>
    </Drawer>
  )
}

export default SelectedShiftDrawer
